import React, {useEffect, useState} from "react";

interface FontPreviewSettings {
    text: string;
    fontSize: string;
    fontWeight: string;
    fontStyle: string;
    textDecoration: string;
    fontFamily: string;
    color: string;
    backgroundColor: string;
}

const defaultSettings: FontPreviewSettings = {
    text: "The quick brown fox jumps over the lazy dog",
    fontSize: "24",
    fontWeight: "normal",
    fontStyle: "normal",
    textDecoration: "none",
    fontFamily: "Arial",
    color: "#FFFFFF",
    backgroundColor: "#2a2d36"
};

interface FontOption {
    value: string;
    label: string;
    category: string;
}

function FontPreview() {
    const [settings, setSettings] = useState<FontPreviewSettings>(defaultSettings);
    const [customText, setCustomText] = useState<string>(defaultSettings.text);
    const [fontCategories, setFontCategories] = useState<{ [key: string]: FontOption[] }>({});

    const fontFamilyOptions: FontOption[] = [
        // Sans-serif fonts
        { value: "Arial", label: "Arial", category: "Sans-serif" },
        { value: "Helvetica", label: "Helvetica", category: "Sans-serif" },
        { value: "Verdana", label: "Verdana", category: "Sans-serif" },
        { value: "Tahoma", label: "Tahoma", category: "Sans-serif" },
        { value: "Trebuchet MS", label: "Trebuchet MS", category: "Sans-serif" },
        { value: "Roboto", label: "Roboto", category: "Sans-serif" },
        { value: "Open Sans", label: "Open Sans", category: "Sans-serif" },
        { value: "Lato", label: "Lato", category: "Sans-serif" },
        { value: "Montserrat", label: "Montserrat", category: "Sans-serif" },
        { value: "Source Sans Pro", label: "Source Sans Pro", category: "Sans-serif" },
        { value: "Noto Sans", label: "Noto Sans", category: "Sans-serif" },
        { value: "Ubuntu", label: "Ubuntu", category: "Sans-serif" },
        
        // Serif fonts
        { value: "Times New Roman", label: "Times New Roman", category: "Serif" },
        { value: "Georgia", label: "Georgia", category: "Serif" },
        { value: "Garamond", label: "Garamond", category: "Serif" },
        { value: "Palatino", label: "Palatino", category: "Serif" },
        { value: "Baskerville", label: "Baskerville", category: "Serif" },
        { value: "Merriweather", label: "Merriweather", category: "Serif" },
        { value: "Playfair Display", label: "Playfair Display", category: "Serif" },
        { value: "Crimson Text", label: "Crimson Text", category: "Serif" },
        { value: "Libre Baskerville", label: "Libre Baskerville", category: "Serif" },
        
        // Monospace fonts
        { value: "Courier New", label: "Courier New", category: "Monospace" },
        { value: "Consolas", label: "Consolas", category: "Monospace" },
        { value: "Monaco", label: "Monaco", category: "Monospace" },
        { value: "Lucida Console", label: "Lucida Console", category: "Monospace" },
        { value: "Source Code Pro", label: "Source Code Pro", category: "Monospace" },
        { value: "Fira Code", label: "Fira Code", category: "Monospace" },
        { value: "JetBrains Mono", label: "JetBrains Mono", category: "Monospace" },
        
        // Display fonts
        { value: "Impact", label: "Impact", category: "Display" },
        { value: "Comic Sans MS", label: "Comic Sans MS", category: "Display" },
        { value: "Pacifico", label: "Pacifico", category: "Display" },
        { value: "Lobster", label: "Lobster", category: "Display" },
        { value: "Bangers", label: "Bangers", category: "Display" },
        { value: "Permanent Marker", label: "Permanent Marker", category: "Display" },
        { value: "Righteous", label: "Righteous", category: "Display" },
        
        // Handwriting fonts
        { value: "Dancing Script", label: "Dancing Script", category: "Handwriting" },
        { value: "Great Vibes", label: "Great Vibes", category: "Handwriting" },
        { value: "Satisfy", label: "Satisfy", category: "Handwriting" },
        { value: "Indie Flower", label: "Indie Flower", category: "Handwriting" },
        { value: "Caveat", label: "Caveat", category: "Handwriting" }
    ];

    useEffect(() => {
        // Load Google Fonts
        const googleFonts = [
            'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Source Sans Pro', 'Noto Sans',
            'Ubuntu', 'Merriweather', 'Playfair Display', 'Crimson Text', 'Libre Baskerville',
            'Source Code Pro', 'Fira Code', 'JetBrains Mono', 'Pacifico', 'Lobster',
            'Bangers', 'Permanent Marker', 'Righteous', 'Dancing Script', 'Great Vibes',
            'Satisfy', 'Indie Flower', 'Caveat'
        ];

        const link = document.createElement('link');
        link.href = `https://fonts.googleapis.com/css2?family=${googleFonts.join('&family=').replace(/ /g, '+')}`;
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        // Organize fonts by category
        const categorizedFonts = fontFamilyOptions.reduce((acc, font) => {
            if (!acc[font.category]) {
                acc[font.category] = [];
            }
            acc[font.category].push(font);
            return acc;
        }, {} as { [key: string]: FontOption[] });

        setFontCategories(categorizedFonts);

        return () => {
            document.head.removeChild(link);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fontWeightOptions = [
        { value: "normal", label: "Normal" },
        { value: "bold", label: "Bold" },
        { value: "100", label: "Thin" },
        { value: "300", label: "Light" },
        { value: "500", label: "Medium" },
        { value: "600", label: "Semi Bold" },
        { value: "800", label: "Extra Bold" },
        { value: "900", label: "Black" }
    ];

    const fontStyleOptions = [
        { value: "normal", label: "Normal" },
        { value: "italic", label: "Italic" },
        { value: "oblique", label: "Oblique" }
    ];

    const textDecorationOptions = [
        { value: "none", label: "None" },
        { value: "underline", label: "Underline" },
        { value: "line-through", label: "Line Through" },
        { value: "overline", label: "Overline" }
    ];

    const handleReset = () => {
        setSettings(defaultSettings);
        setCustomText(defaultSettings.text);
    };

    return (
        <div className="tool">
            <h2>Font Preview</h2>
            <div className="content">
                <div className="font-preview">
                    <div className="font-preview__input-section">
                        <div 
                            className="font-preview__preview"
                            style={{ backgroundColor: settings.backgroundColor }}
                        >
                            <div 
                                className="font-preview__text"
                                style={{
                                    fontSize: `${settings.fontSize}px`,
                                    fontWeight: settings.fontWeight,
                                    fontStyle: settings.fontStyle,
                                    textDecoration: settings.textDecoration,
                                    fontFamily: settings.fontFamily,
                                    color: settings.color
                                }}
                            >
                                {settings.text || "Enter some text to preview"}
                            </div>
                        </div>

                        <div className="font-preview__controls-container">
                            <div className="font-preview__section">
                                <h3 className="font-preview__section-title">Text Content</h3>
                                <div className="font-preview__input-container">
                                    <input
                                        type="text"
                                        value={customText}
                                        onChange={(e) => {
                                            setCustomText(e.target.value);
                                            setSettings(prev => ({ ...prev, text: e.target.value }));
                                        }}
                                        placeholder="Enter text to preview"
                                        className="font-preview__input"
                                    />
                                    <button 
                                        onClick={handleReset}
                                        className="font-preview__clear-button"
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>

                            <div className="font-preview__section">
                                <h3 className="font-preview__section-title">Font Properties</h3>
                                <div className="font-preview__controls">
                                    <div className="font-preview__control-group font-preview__control-group--full">
                                        <label>Font Family</label>
                                        <select
                                            value={settings.fontFamily}
                                            onChange={(e) => setSettings(prev => ({ ...prev, fontFamily: e.target.value }))}
                                            className="font-preview__select"
                                            style={{ fontFamily: settings.fontFamily }}
                                        >
                                            {Object.entries(fontCategories).map(([category, fonts]) => (
                                                <optgroup key={category} label={category}>
                                                    {fonts.map(font => (
                                                        <option 
                                                            key={font.value} 
                                                            value={font.value}
                                                            style={{ fontFamily: font.value }}
                                                        >
                                                            {font.label}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="font-preview__control-group font-preview__control-group--size">
                                        <label>Font Size (px)</label>
                                        <div className="font-preview__size-control">
                                            <input
                                                type="number"
                                                value={settings.fontSize}
                                                onChange={(e) => setSettings(prev => ({ ...prev, fontSize: e.target.value }))}
                                                min="8"
                                                max="100"
                                                className="font-preview__input"
                                            />
                                            <input
                                                type="range"
                                                value={settings.fontSize}
                                                onChange={(e) => setSettings(prev => ({ ...prev, fontSize: e.target.value }))}
                                                min="8"
                                                max="100"
                                                className="font-preview__range"
                                            />
                                        </div>
                                    </div>

                                    <div className="font-preview__control-group">
                                        <label>Font Weight</label>
                                        <select
                                            value={settings.fontWeight}
                                            onChange={(e) => setSettings(prev => ({ ...prev, fontWeight: e.target.value }))}
                                            className="font-preview__select"
                                        >
                                            {fontWeightOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="font-preview__control-group">
                                        <label>Font Style</label>
                                        <select
                                            value={settings.fontStyle}
                                            onChange={(e) => setSettings(prev => ({ ...prev, fontStyle: e.target.value }))}
                                            className="font-preview__select"
                                        >
                                            {fontStyleOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="font-preview__control-group">
                                        <label>Text Decoration</label>
                                        <select
                                            value={settings.textDecoration}
                                            onChange={(e) => setSettings(prev => ({ ...prev, textDecoration: e.target.value }))}
                                            className="font-preview__select"
                                        >
                                            {textDecorationOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="font-preview__section">
                                <h3 className="font-preview__section-title">Colors</h3>
                                <div className="font-preview__controls">
                                    <div className="font-preview__control-group">
                                        <label>Text Color</label>
                                        <div className="font-preview__color-control">
                                            <input
                                                type="color"
                                                value={settings.color}
                                                onChange={(e) => setSettings(prev => ({ ...prev, color: e.target.value }))}
                                                className="font-preview__color-input"
                                            />
                                            <input
                                                type="text"
                                                value={settings.color}
                                                onChange={(e) => setSettings(prev => ({ ...prev, color: e.target.value }))}
                                                className="font-preview__input"
                                                placeholder="#FFFFFF"
                                            />
                                        </div>
                                    </div>

                                    <div className="font-preview__control-group">
                                        <label>Background Color</label>
                                        <div className="font-preview__color-control">
                                            <input
                                                type="color"
                                                value={settings.backgroundColor}
                                                onChange={(e) => setSettings(prev => ({ ...prev, backgroundColor: e.target.value }))}
                                                className="font-preview__color-input"
                                            />
                                            <input
                                                type="text"
                                                value={settings.backgroundColor}
                                                onChange={(e) => setSettings(prev => ({ ...prev, backgroundColor: e.target.value }))}
                                                className="font-preview__input"
                                                placeholder="#2a2d36"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="font-preview__css-code">
                        <strong>CSS Code:</strong>
                        <pre>
{`font-family: ${settings.fontFamily};
font-size: ${settings.fontSize}px;
font-weight: ${settings.fontWeight};
font-style: ${settings.fontStyle};
text-decoration: ${settings.textDecoration};
color: ${settings.color};
background-color: ${settings.backgroundColor};`}
                        </pre>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FontPreview; 